import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './Request.css';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Chip,
  Menu,
  MenuItem,
  Button,
  Input,
  FormControl,
  Select,
  Popover,
  Paper,
  Popper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
} from '@mui/material';
import usePagination from '@mui/material/usePagination';
import RequestTable from '../../../../components/Admin/Request/Academics/RequestTable';
import CreativeArtsRequestTable from '../../../../components/Admin/Request/CreativeArts/CreativeArtsRequestTable';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import { axiosInstance } from '../../../../utils/tokenUtils';
import FilterIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';
import ColoredFilterIcon from '../../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { TableSkeleton } from '../../../../components/Admin/SkeletonLoader/SkeletonLoader';

const SUBJECT_PAGE_SIZE = 30;

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`request-tabpanel-${index}`}
      aria-labelledby={`request-tab-${index}`}
      sx={{
        flex: 1,
        display: value !== index ? 'none' : 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const AcademicLevelArray = [
  { value: 'All', label: 'All' },
  { value: 'elementary', label: 'Elementary' },
  { value: 'high_school', label: 'High School' },
  { value: 'middle_school', label: 'Middle School' },
  { value: 'university', label: 'University' },
];

const CreativeLevelArray = [
  { value: 'All', label: 'All' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'professional', label: 'Professional' },
];

const CategoryArray = [
  { value: 'All', label: 'All' },
  { value: 'teacher', label: 'Instructors' },
  { value: 'student_as_teacher', label: 'Learner As Instructor' },
];

const StatusArray = [
  { value: 'All', label: 'All' },
  { value: 'approval_pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'payment_pending', label: 'Payment Pending' },
  { value: 'requested_for_reverification', label: 'Requested For Reverification' },
  { value: 'application_not_started', label: 'Application Not Started' },
  { value: 'application_not_completed', label: 'Application Not Completed' },
];

// Statuses that represent teachers who have not completed payment, so the
// has_paid=true filter must not be applied for them (it would return nothing).
const NON_PAID_STATUSES = [
  'payment_pending',
  'requested_for_reverification',
  'application_not_started',
  'application_not_completed',
];

const SessionTypeArray = [
  { value: 'All', label: 'All' },
  { value: 'one_on_one', label: 'One on One Session' },
  { value: 'group', label: 'Group Session' },
  { value: 'prime', label: 'Prime Session' },
];

const GenderArray = [
  { value: 'All', label: 'All' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

// Generate experience array (1-10 + More than 10)
const ExperienceArray = [
  { value: 'All', label: 'All' },
  { value: '1-3', label: '1-3 years' },
  { value: '4-6', label: '4-6 years' },
  { value: '7-9', label: '7-9 years' },
  { value: '10+', label: '10+ years' },
];

// Utility function to format text
const formatText = text => {
  if (!text) return text;
  return text
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Utility function to convert subject title to snake_case for API
const convertToSnakeCase = text => {
  if (!text) return text;
  return text
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
};

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const Request = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [teacherAllData, setTeacherAllData] = useState([]);
  const [creativeArtsData, setCreativeArtsData] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);

  // Applied filter states (used for API calls)
  const [appliedLevelToTeach, setAppliedLevelToTeach] = useState('All');
  const [appliedCategory, setAppliedCategory] = useState('All');
  const [appliedSessionType, setAppliedSessionType] = useState('All');
  const [appliedStatusFilter, setAppliedStatusFilter] = useState('All');
  const [appliedSelectedSubject, setAppliedSelectedSubject] = useState('All');
  const [appliedSelectedTags, setAppliedSelectedTags] = useState([]);
  const [appliedExperience, setAppliedExperience] = useState('All');
  const [appliedLanguage, setAppliedLanguage] = useState('All');
  const [appliedCity, setAppliedCity] = useState('All');
  const [appliedCountry, setAppliedCountry] = useState('All');
  const [appliedGender, setAppliedGender] = useState('All');
  const [appliedCouponCode, setAppliedCouponCode] = useState('All');

  // Pending filter states (used for UI display)
  const [levelToTeach, setLevelToTeach] = useState('All');
  const [category, setCategory] = useState('All');
  const [sessionType, setSessionType] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [selectedTags, setSelectedTags] = useState([]);
  const [experience, setExperience] = useState('All');
  const [language, setLanguage] = useState('All');
  const [city, setCity] = useState('All');
  const [country, setCountry] = useState('All');
  const [gender, setGender] = useState('All');
  const [couponCode, setCouponCode] = useState('All');

  // Bulk selection states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Pagination states
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Data states
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [subjectList, setSubjectList] = useState([]);
  const [subjectPage, setSubjectPage] = useState(1);
  const [subjectTotalCount, setSubjectTotalCount] = useState(0);
  const [subjectHasMore, setSubjectHasMore] = useState(true);
  const [subjectLoadingMore, setSubjectLoadingMore] = useState(false);
  const [tagList, setTagList] = useState([]);
  const [languageList, setLanguageList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [couponList, setCouponList] = useState([]);

  // Coupon loading states
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponPage, setCouponPage] = useState(1);
  const [hasMoreCoupons, setHasMoreCoupons] = useState(true);

  // Whether the current admin can view coupons (gates the Coupon Code filter).
  // Roles without `view_coupons` (e.g. Talent Manager) would otherwise hit a
  // 403 on /admin/coupons and trigger a "no permission" toast on filter open.
  const canViewCoupons = useMemo(() => {
    try {
      const me = JSON.parse(sessionStorage.getItem('rbac_admin_me') || '{}');
      const roles = Array.isArray(me.roles) ? me.roles : [];
      const permissions = Array.isArray(me.permissions) ? me.permissions : [];
      return (
        roles.includes('super_admin') ||
        roles.includes('admin') ||
        permissions.includes('view_coupons')
      );
    } catch {
      return false;
    }
  }, []);

  // Tag dropdown state
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [tagAnchorEl, setTagAnchorEl] = useState(null);
  const tagDropdownRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);

  // Track initial load per tab to control when to show blocking loader
  const [initialLoadDoneByTab, setInitialLoadDoneByTab] = useState({ 0: false, 1: false });
  const [isFetching, setIsFetching] = useState(false);

  // Create refs to access current values without triggering re-renders in fetchRequestData
  const subjectListRef = useRef([]);
  const tagListRef = useRef([]);

  // Track which filter lists have been loaded to prevent duplicate calls
  const filtersLoadedRef = useRef({
    languages: false,
    countries: false,
    coupons: false,
  });

  // Debounced search term to avoid spamming the API
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Sorting states (copying UI and logic from Teacher.jsx)
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Update refs when state changes
  useEffect(() => {
    subjectListRef.current = subjectList;
  }, [subjectList]);
  useEffect(() => {
    tagListRef.current = tagList;
  }, [tagList]);

  // API Functions
  const fetchSubjects = useCallback(async (subjectType, page = 1, size = SUBJECT_PAGE_SIZE) => {
    try {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (subjectType) {
        params.append('subject_type', subjectType);
      }
      const { data } = await axiosInstance.get(`/common/subjects?${params.toString()}`);
      return data || { results: [], count: 0 };
    } catch (error) {
      console.log('Error fetching subjects:', error);
      return { results: [], count: 0 };
    }
  }, []);

  const loadInitialSubjects = useCallback(
    async subjectType => {
      const { results, count } = await fetchSubjects(subjectType, 1, SUBJECT_PAGE_SIZE);
      const list = results || [];
      setSubjectList(list);
      setSubjectPage(1);
      setSubjectTotalCount(count || 0);
      setSubjectHasMore(list.length < (count || 0));
    },
    [fetchSubjects]
  );

  const loadMoreSubjects = useCallback(async () => {
    if (subjectLoadingMore || !subjectHasMore) return;
    setSubjectLoadingMore(true);
    try {
      const nextPage = subjectPage + 1;
      const subjectType = tabValue === 0 ? 'academic' : 'creative';
      const { results: nextSubjects, count } = await fetchSubjects(
        subjectType,
        nextPage,
        SUBJECT_PAGE_SIZE
      );
      const total = count || subjectTotalCount;
      setSubjectTotalCount(total);
      setSubjectList(prev => {
        const existingIds = new Set(prev.map(s => s.id));
        const merged = [...prev];
        (nextSubjects || []).forEach(s => {
          if (!existingIds.has(s.id)) merged.push(s);
        });
        setSubjectHasMore(merged.length < total);
        return merged;
      });
      setSubjectPage(nextPage);
    } catch (error) {
      console.log('Error loading more subjects:', error);
    } finally {
      setSubjectLoadingMore(false);
    }
  }, [subjectLoadingMore, subjectHasMore, subjectPage, subjectTotalCount, tabValue, fetchSubjects]);

  const handleSubjectMenuScroll = event => {
    const list = event.currentTarget;
    if (!list) return;
    const nearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 8;
    if (nearBottom) {
      loadMoreSubjects();
    }
  };

  // Render the correct table based on tabValue and pass the correct data
  const renderTable = () => {
    if (tabValue === 0) {
      return <RequestTable teacherRequestData={teacherAllData} />;
    }
    return <CreativeArtsRequestTable teacherRequestData={creativeArtsData} />;
  };

  const fetchTags = async subjectId => {
    try {
      const { data } = await axiosInstance.get(`/common/${subjectId}/tags`);
      setTagList(data && Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.log('Error fetching tags:', error);
      setTagList([]);
    }
  };

  const fetchLanguages = async (searchQuery = '') => {
    try {
      const url = searchQuery ? `/auth/languages?search_query=${searchQuery}` : '/auth/languages';
      const { data } = await axiosInstance.get(url);
      setLanguageList(data || []);
    } catch (error) {
      console.log('Error fetching languages:', error);
      setLanguageList([]);
    }
  };

  const fetchCountries = async (searchQuery = '') => {
    try {
      const url = searchQuery ? `/auth/countries?search_query=${searchQuery}` : '/auth/countries';
      const { data } = await axiosInstance.get(url);
      setCountryList(data || []);
    } catch (error) {
      console.log('Error fetching countries:', error);
      setCountryList([]);
    }
  };

  const fetchCities = async countryId => {
    try {
      const { data } = await axiosInstance.get(`/auth/countries/${countryId}/cities`);
      setCityList(data || []);
    } catch (error) {
      console.log('Error fetching cities:', error);
      setCityList([]);
    }
  };

  const fetchCoupons = async (page = 1) => {
    try {
      setCouponLoading(true);
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '15');
      params.append('include_deleted', 'true');

      const { data } = await axiosInstance.get(`/admin/coupons?${params.toString()}`);

      if (page === 1) {
        // First load - replace the list
        setCouponList(data?.results || []);
      } else {
        setCouponList(prev => [...prev, ...(data?.results || [])]);
      }

      setCouponPage(page + 1);
      setHasMoreCoupons((data?.results || []).length === 15);
    } catch (error) {
      console.log('Error fetching coupons:', error);
      if (page === 1) {
        setCouponList([]);
      }
      setCouponPage(1);
      setHasMoreCoupons(false);
    } finally {
      setCouponLoading(false);
    }
  };

  const loadMoreCoupons = () => {
    if (!couponLoading && hasMoreCoupons) {
      fetchCoupons(couponPage);
    }
  };

  const fetchRequestDataForTab = useCallback(
    async targetTabValue => {
      try {
        const isInitialLoad = !initialLoadDoneByTab[targetTabValue];
        if (isInitialLoad) {
          setLoading(true);
        } else {
          setIsFetching(true);
        }

        // Build params for API call
        const params = new URLSearchParams();
        params.append('teacher_type', targetTabValue === 0 ? 'academic' : 'creative');
        // "All" must include unpaid statuses too — only send has_paid for paid status filters
        if (appliedStatusFilter !== 'All' && !NON_PAID_STATUSES.includes(appliedStatusFilter)) {
          params.append('has_paid', 'true');
        }

        // Add pagination parameters
        params.append('page', page.toString());
        params.append('page_size', rowsPerPage.toString());

        if (appliedStatusFilter !== 'All') {
          params.append('approval_status', appliedStatusFilter);
        }

        if (debouncedSearchTerm) params.append('query', debouncedSearchTerm);
        if (appliedLevelToTeach !== 'All') params.append('level_to_teach', appliedLevelToTeach);
        if (appliedCategory !== 'All') params.append('category', appliedCategory);
        if (appliedSessionType !== 'All') params.append('session_type', appliedSessionType);
        if (appliedSelectedSubject !== 'All') {
          // Use the selected subject title directly
          params.append('subject_name', convertToSnakeCase(appliedSelectedSubject));
        }
        if (appliedSelectedTags.length > 0) {
          const tagNames = appliedSelectedTags
            .map(tagId => {
              const tag = tagListRef.current.find(t => t.id === tagId);
              return tag ? tag.title : '';
            })
            .filter(Boolean);
          if (tagNames.length > 0) {
            params.append('tag_names', tagNames.join(','));
          }
        }
        if (appliedExperience !== 'All') params.append('years_of_experience', appliedExperience);
        if (appliedLanguage !== 'All') params.append('teaching_language', appliedLanguage);
        if (appliedCountry !== 'All') params.append('country', appliedCountry);
        if (appliedCity !== 'All') params.append('city', appliedCity);
        if (appliedGender !== 'All') params.append('gender', appliedGender);
        if (appliedCouponCode !== 'All') params.append('coupon_code', appliedCouponCode);

        // Sorting (same as Teacher.jsx)
        if (sortBy) {
          const sortParam = `${sortOrder === 'desc' ? '-' : ''}${sortBy}`;
          params.append('sort_by', sortParam);
        }

        // Make API call
        const { data } = await axiosInstance.get(`/admin/teachers?${params.toString()}`);

        // Extract items from the API response structure and use API order directly
        const finalData = data?.results || [];

        // Set total count from API response
        setTotalCount(data?.count || 0);

        console.log('API Response items count:', finalData.length);
        console.log('Total items from API:', data?.count || 0);
        console.log('Current page:', data?.page || 1);
        console.log('Total pages:', data?.total_pages || 1);

        // Update state with fresh data
        if (targetTabValue === 0) {
          setTeacherAllData(finalData);
        } else {
          setCreativeArtsData(finalData);
        }
      } catch (error) {
        console.log('Error fetching request data:', error);
        // Keep existing data on error to avoid UI flicker
      } finally {
        const isInitialLoad = !initialLoadDoneByTab[targetTabValue];
        if (isInitialLoad) {
          setInitialLoadDoneByTab(prev => ({ ...prev, [targetTabValue]: true }));
          setLoading(false);
        }
        setIsFetching(false);
      }
    },
    [
      debouncedSearchTerm,
      appliedLevelToTeach,
      appliedCategory,
      appliedSessionType,
      appliedStatusFilter,
      appliedSelectedSubject,
      appliedSelectedTags,
      appliedExperience,
      appliedLanguage,
      appliedCountry,
      appliedCity,
      appliedGender,
      appliedCouponCode,
      page,
      rowsPerPage,
      initialLoadDoneByTab,
      sortBy,
      sortOrder,
    ]
  );

  // Get data for display - no more client-side coupon filtering
  const getCurrentData = useCallback(() => {
    return tabValue === 0 ? teacherAllData : creativeArtsData;
  }, [tabValue, teacherAllData, creativeArtsData]);

  const fetchRequestData = useCallback(async () => {
    return fetchRequestDataForTab(tabValue);
  }, [fetchRequestDataForTab, tabValue]);

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
    // Reset filters when switching tabs
    setLevelToTeach('All');
    setSelectedSubject('All');
    setSelectedTags([]);
    setExperience('All');
    setCouponCode('All');

    // Also reset the applied filters for these dependent fields
    setAppliedLevelToTeach('All');
    setAppliedSelectedSubject('All');
    setAppliedSelectedTags([]);

    // Reset pagination when switching tabs
    setPage(1);
    // Do not prefetch filter lists here to avoid duplicate/unwanted calls
    // Teacher list will refresh via the consolidated effect
  }, []);

  // Consolidated effect: fetch teacher list when relevant inputs change (debounced)
  useEffect(() => {
    fetchRequestData();
  }, [fetchRequestData]);

  // Remove initial eager filter loads; filters will be lazy-loaded on open

  // Add click outside handler for tag dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        tagAnchorEl &&
        !tagAnchorEl.contains(event.target) &&
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target)
      ) {
        setTagDropdownOpen(false);
        setTagAnchorEl(null);
      }
    };

    if (tagDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tagDropdownOpen, tagAnchorEl]);

  // Fetch cities based on active country selection (pending while filter open, otherwise applied)
  useEffect(() => {
    const activeCountry = openFilter ? country : appliedCountry;
    if (activeCountry !== 'All') {
      const countryObj = countryList.find(c => c.name === activeCountry);
      if (countryObj) {
        fetchCities(countryObj.id);
      }
    } else {
      setCityList([]);
      if (openFilter) setCity('All');
    }
  }, [openFilter, country, appliedCountry, countryList]);

  // Fetch tags based on active subject selection (pending while filter open, otherwise applied)
  useEffect(() => {
    const activeSubject = openFilter ? selectedSubject : appliedSelectedSubject;
    if (activeSubject !== 'All') {
      const subject = subjectList.find(s => s.title === activeSubject);
      if (subject) {
        fetchTags(subject.id);
      }
    } else {
      setTagList([]);
      if (openFilter) setSelectedTags([]);
    }
  }, [openFilter, selectedSubject, appliedSelectedSubject, subjectList]);

  // Event Handlers
  const handleClickToOpenFilter = async event => {
    setAnchorEl(event.currentTarget);
    setOpenFilter(true);

    // Lazy-load filter lists, only once each; update quietly without affecting main table loading
    const tasks = [];

    if (!filtersLoadedRef.current.languages) {
      tasks.push(
        fetchLanguages().finally(() => {
          filtersLoadedRef.current.languages = true;
        })
      );
    }

    if (!filtersLoadedRef.current.countries) {
      tasks.push(
        fetchCountries().finally(() => {
          filtersLoadedRef.current.countries = true;
        })
      );
    }

    const subjectType = tabValue === 0 ? 'academic' : 'creative';
    tasks.push(loadInitialSubjects(subjectType));

    if (canViewCoupons && !filtersLoadedRef.current.coupons) {
      tasks.push(
        fetchCoupons(1).finally(() => {
          filtersLoadedRef.current.coupons = true;
        })
      );
    }

    // If a subject is already selected in the filter, ensure tags are loaded
    const currentSubject = selectedSubject;
    if (currentSubject !== 'All') {
      const subject = subjectListRef.current.find(s => s.title === currentSubject);
      if (subject) {
        tasks.push(fetchTags(subject.id));
      }
    }

    if (tasks.length > 0) {
      try {
        await Promise.all(tasks);
      } catch (e) {
        // Already logged in individual fetchers
      }
    }
  };

  const handleApplyFilter = () => {
    setOpenFilter(false);
    setAnchorEl(null);
    // Apply filters by fetching data with current filter values
    setAppliedLevelToTeach(levelToTeach);
    setAppliedCategory(category);
    setAppliedSessionType(sessionType);
    setAppliedStatusFilter(statusFilter);
    setAppliedSelectedSubject(selectedSubject);
    setAppliedSelectedTags(selectedTags);
    setAppliedExperience(experience);
    setAppliedLanguage(language);
    setAppliedCity(city);
    setAppliedCountry(country);
    setAppliedGender(gender);
    setAppliedCouponCode(couponCode);

    // Reset pagination when filters are applied
    setPage(1);
    // The consolidated effect will trigger the data fetch; avoid calling it directly to prevent duplicates
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
    setOpenFilter(false);
  };

  const handleClearAll = () => {
    // Clear pending filters only; applied filters are updated on Apply
    setLevelToTeach('All');
    setCategory('All');
    setSessionType('All');
    setStatusFilter('All');
    setSelectedSubject('All');
    setSelectedTags([]);
    setExperience('All');
    setLanguage('All');
    setCity('All');
    setCountry('All');
    setGender('All');
    setCouponCode('All');
  };

  const handleChangeSearch = e => {
    setSearchTerm(e.target.value);
  };

  // Get appropriate level array based on selected tab
  const getLevelArray = () => {
    return tabValue === 0 ? AcademicLevelArray : CreativeLevelArray;
  };

  // Tag handlers
  const handleTagDropdownClick = event => {
    setTagAnchorEl(event.currentTarget);
    setTagDropdownOpen(!tagDropdownOpen);
  };

  const handleTagDropdownClose = () => {
    setTagDropdownOpen(false);
    setTagAnchorEl(null);
  };

  const handleTagSelection = tagId => {
    setSelectedTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
  };

  // Get display text for tags
  const getTagDisplayText = () => {
    if (selectedTags.length === 0) return 'All';

    const selectedTagNames = selectedTags
      .map(tagId => {
        const tag = tagList.find(t => t.id === tagId);
        return tag ? tag.title : '';
      })
      .filter(Boolean);

    if (selectedTagNames.length <= 2) {
      return selectedTagNames.join(', ');
    } else {
      return `${selectedTagNames.slice(0, 2).join(', ')} +${selectedTagNames.length - 2} more`;
    }
  };

  // Function to get active filters
  const getActiveFilters = () => {
    const filters = [];

    if (appliedLevelToTeach !== 'All') {
      filters.push({
        type: 'levelToTeach',
        label: `Level: ${formatText(appliedLevelToTeach)}`,
        value: appliedLevelToTeach,
      });
    }

    if (appliedCategory !== 'All') {
      filters.push({
        type: 'category',
        label: `Category: ${formatText(appliedCategory)}`,
        value: appliedCategory,
      });
    }

    if (appliedSessionType !== 'All') {
      filters.push({
        type: 'sessionType',
        label: `Session Type: ${formatText(appliedSessionType.replace(/_/g, ' '))}`,
        value: appliedSessionType,
      });
    }

    if (appliedStatusFilter !== 'All') {
      const statusLabel =
        StatusArray.find(s => s.value === appliedStatusFilter)?.label ||
        formatText(appliedStatusFilter);
      filters.push({
        type: 'statusFilter',
        label: `Status: ${statusLabel}`,
        value: appliedStatusFilter,
      });
    }

    if (appliedSelectedSubject !== 'All') {
      filters.push({
        type: 'selectedSubject',
        label: `Subject: ${appliedSelectedSubject}`,
        value: appliedSelectedSubject,
      });
    }

    if (appliedSelectedTags.length > 0) {
      const tagNames = appliedSelectedTags
        .map(tagId => {
          const tag = tagList.find(t => t.id === tagId);
          return tag ? tag.title : '';
        })
        .filter(Boolean);

      filters.push({
        type: 'selectedTags',
        label: `Tags: ${tagNames.join(', ')}`,
        value: appliedSelectedTags,
      });
    }

    if (appliedExperience !== 'All') {
      const experienceLabel =
        appliedExperience === '10+' ? '10+ years' : `${appliedExperience} years`;
      filters.push({
        type: 'experience',
        label: `Experience: ${experienceLabel}`,
        value: appliedExperience,
      });
    }

    if (appliedLanguage !== 'All') {
      filters.push({
        type: 'language',
        label: `Language: ${appliedLanguage}`,
        value: appliedLanguage,
      });
    }

    if (appliedCountry !== 'All') {
      filters.push({
        type: 'country',
        label: `Country: ${appliedCountry}`,
        value: appliedCountry,
      });
    }

    if (appliedCity !== 'All') {
      filters.push({
        type: 'city',
        label: `City: ${appliedCity}`,
        value: appliedCity,
      });
    }

    if (appliedGender !== 'All') {
      filters.push({
        type: 'gender',
        label: `Gender: ${formatText(appliedGender)}`,
        value: appliedGender,
      });
    }

    if (appliedCouponCode !== 'All') {
      filters.push({
        type: 'couponCode',
        label: `Coupon Code: ${appliedCouponCode}`,
        value: appliedCouponCode,
      });
    }

    return filters;
  };

  // Handler for removing a filter
  const handleRemoveFilter = (filterType, filterValue) => {
    switch (filterType) {
      case 'levelToTeach':
        setLevelToTeach('All');
        setAppliedLevelToTeach('All');
        break;
      case 'category':
        setCategory('All');
        setAppliedCategory('All');
        break;
      case 'sessionType':
        setSessionType('All');
        setAppliedSessionType('All');
        break;
      case 'statusFilter':
        setStatusFilter('All');
        setAppliedStatusFilter('All');
        break;
      case 'selectedSubject':
        setSelectedSubject('All');
        setSelectedTags([]);
        setAppliedSelectedSubject('All');
        setAppliedSelectedTags([]);
        break;
      case 'selectedTags':
        setSelectedTags([]);
        setAppliedSelectedTags([]);
        break;
      case 'experience':
        setExperience('All');
        setAppliedExperience('All');
        break;
      case 'language':
        setLanguage('All');
        setAppliedLanguage('All');
        break;
      case 'country':
        setCountry('All');
        setCity('All');
        setAppliedCountry('All');
        setAppliedCity('All');
        break;
      case 'city':
        setCity('All');
        setAppliedCity('All');
        break;
      case 'gender':
        setGender('All');
        setAppliedGender('All');
        break;
      case 'couponCode':
        setCouponCode('All');
        setAppliedCouponCode('All');
        break;
      default:
        break;
    }

    // Reset pagination when filters are removed
    setPage(1);
    // Consolidated effect will refresh data automatically
  };

  // Bulk selection handlers
  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    if (selectionMode) {
      // Leaving selection mode – clear selections
      setSelectedIds(new Set());
    }
  };

  // Handlers passed to table components
  const handleSelectOne = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSelectAllVisible = (ids, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  // Pagination handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(event.target.value);
    setPage(1); // Reset to first page when changing page size
  };

  // Pagination hook
  const { items } = usePagination({
    count: Math.ceil(totalCount / rowsPerPage),
    page: page,
    onChange: handlePageChange,
  });

  return (
    <Layout title="Klass Online | Request">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-4">
            <Box className="flex justify-between items-center">
              <Typography className="request-title">Requests</Typography>
              <Box className="flex items-center gap-4 w-[40%]">
                <Input
                  sx={{
                    textDecoration: 'none',
                    border: '1px solid var(--Stroke, #D5DEE3)',
                    borderRadius: '56px',
                    '&:before, &:after': {
                      borderBottom: 'none !important',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: 'none !important',
                    },
                    padding: '8px 10px',
                    '& .MuiInputBase-input': {
                      padding: '3px 5px 3px 5px',
                    },
                    background: '#FFF',
                    width: '100%',
                    height: '40px',
                  }}
                  type="search"
                  placeholder="Search"
                  aria-label="Search"
                  value={searchTerm}
                  onChange={handleChangeSearch}
                  startAdornment={<SearchIcon className="search-icon-css" />}
                />
                {/* Removed Select button for always-on selection mode */}
                {openFilter ? (
                  <img
                    src={ColoredFilterIcon}
                    alt="Filter-icon"
                    style={{ width: '25px', height: '25px', cursor: 'pointer' }}
                    onClick={handleClickToOpenFilter}
                  />
                ) : (
                  <img
                    src={FilterIcon}
                    alt="Filter-icon"
                    style={{ width: '25px', height: '25px', cursor: 'pointer' }}
                    onClick={handleClickToOpenFilter}
                  />
                )}
              </Box>
            </Box>

            {/* Filter Modal */}
            <Popover
              open={openFilter}
              anchorEl={anchorEl}
              onClose={handleCloseFilter}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                className: 'filter-modal-container',
              }}
            >
              {/* Header */}
              <Box className="filter-modal-header">
                <Typography className="filter-modal-title">Filters</Typography>
              </Box>

              {/* Scrollable Content */}
              <Box className="filter-modal-content">
                {/* Grid for filter items */}
                <Box className="filter-grid">
                  {/* Level to Teach Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Level to Teach</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={levelToTeach}
                        onChange={e => setLevelToTeach(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {getLevelArray().map(level => (
                          <MenuItem
                            key={level.value}
                            value={level.value}
                            className="dropdown-option"
                          >
                            {level.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Category Filter - Only for Academic */}
                  {/* {tabValue === 0 && (
                    <Box className="filter-item">
                      <label className="filter-label">Category</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={category}
                          onChange={e => setCategory(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          {CategoryArray.map(cat => (
                            <MenuItem key={cat.value} value={cat.value} className="dropdown-option">
                              {cat.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )} */}

                  {/* Session Type Filter */}
                  {/* <Box className="filter-item">
                    <label className="filter-label">Session Type</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={sessionType}
                        onChange={e => setSessionType(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {SessionTypeArray.map(type => (
                          <MenuItem key={type.value} value={type.value} className="dropdown-option">
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box> */}

                  {/* Subject Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Subject</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={selectedSubject}
                        onChange={e => setSelectedSubject(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                              maxHeight: 200,
                            },
                          },
                          MenuListProps: {
                            onScroll: handleSubjectMenuScroll,
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        <MenuItem value="All" className="dropdown-option">
                          All
                        </MenuItem>
                        {subjectList.map(subject => (
                          <MenuItem
                            key={subject.id}
                            value={subject.title}
                            className="dropdown-option"
                          >
                            {formatText(subject.title)}
                          </MenuItem>
                        ))}
                        {subjectLoadingMore && (
                          <MenuItem disabled className="dropdown-option">
                            Loading...
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Tag Filter - Multi-select with radio buttons */}
                  {selectedSubject !== 'All' && (
                    <Box className="filter-item">
                      <label className="filter-label">
                        Tag {selectedTags.length > 0 && `(${selectedTags.length} selected)`}
                      </label>
                      <Box
                        className="filter-input"
                        onClick={handleTagDropdownClick}
                        sx={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: selectedTags.length === 0 ? '#949DA6' : '#14171f',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {getTagDisplayText()}
                        </Typography>
                        <KeyboardArrowDownIcon sx={{ color: '#949DA6' }} />
                      </Box>

                      {/* Custom Tag Dropdown */}
                      <Popper
                        open={tagDropdownOpen}
                        anchorEl={tagAnchorEl}
                        placement="bottom-start"
                        style={{ zIndex: 1500, width: tagAnchorEl?.offsetWidth }}
                      >
                        <Paper
                          ref={tagDropdownRef}
                          sx={{
                            maxHeight: 200,
                            overflow: 'auto',
                            border: '1px solid #D5DEE3',
                            borderRadius: '8px',
                            mt: 0.5,
                          }}
                        >
                          <List dense>
                            {/* Clear All Tags Option */}
                            {selectedTags.length > 0 && (
                              <ListItem
                                button
                                onClick={() => setSelectedTags([])}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(231, 76, 60, 0.08)',
                                  },
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                }}
                              >
                                <ListItemText
                                  primary="Clear All Tags"
                                  sx={{
                                    '& .MuiListItemText-primary': {
                                      fontSize: '13px',
                                      fontFamily: 'Poppins',
                                      color: '#e74c3c',
                                      fontWeight: 500,
                                    },
                                  }}
                                />
                              </ListItem>
                            )}

                            {/* Tag Options */}
                            {tagList.map(tag => (
                              <ListItem
                                key={tag.id}
                                button
                                onClick={e => {
                                  e.stopPropagation();
                                  handleTagSelection(tag.id);
                                }}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(10, 94, 243, 0.08)',
                                  },
                                  cursor: 'pointer',
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <Checkbox
                                    checked={selectedTags.includes(tag.id)}
                                    size="small"
                                    sx={{
                                      color: '#949DA6',
                                      '&.Mui-checked': {
                                        color: '#0A5EF3',
                                      },
                                    }}
                                    onClick={e => {
                                      e.stopPropagation();
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={formatText(tag.title)}
                                  sx={{
                                    '& .MuiListItemText-primary': {
                                      fontSize: '13px',
                                      fontFamily: 'Poppins',
                                    },
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Popper>
                    </Box>
                  )}

                  {/* Status Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Status</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {StatusArray.map(status => (
                          <MenuItem
                            key={status.value}
                            value={status.value}
                            className="dropdown-option"
                          >
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Experience Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Years of Experience</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={experience}
                        onChange={e => setExperience(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {ExperienceArray.map(item => (
                          <MenuItem key={item.value} value={item.value} className="dropdown-option">
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Language Filter - Changed to Select */}
                  <Box className="filter-item">
                    <label className="filter-label">Instructing Language</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={language}
                        onChange={e => setLanguage(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        <MenuItem value="All" className="dropdown-option">
                          All
                        </MenuItem>
                        {languageList.map(lang => (
                          <MenuItem key={lang.id} value={lang.name} className="dropdown-option">
                            {lang.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Country Filter - Changed to Select */}
                  <Box className="filter-item">
                    <label className="filter-label">Country</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={country}
                        onChange={e => setCountry(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        <MenuItem value="All" className="dropdown-option">
                          All
                        </MenuItem>
                        {countryList.map(countryItem => (
                          <MenuItem
                            key={countryItem.id}
                            value={countryItem.name}
                            className="dropdown-option"
                          >
                            {countryItem.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* City Filter - Changed to Select - Only show when country is selected */}
                  {country !== 'All' && (
                    <Box className="filter-item">
                      <label className="filter-label">City</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={city}
                          onChange={e => setCity(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 200,
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value="All" className="dropdown-option">
                            All
                          </MenuItem>
                          {cityList.map(cityItem => (
                            <MenuItem
                              key={cityItem.id}
                              value={cityItem.name}
                              className="dropdown-option"
                            >
                              {cityItem.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Gender Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Gender</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={gender}
                        onChange={e => setGender(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {GenderArray.map(item => (
                          <MenuItem key={item.value} value={item.value} className="dropdown-option">
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Coupon Code Filter */}
                  {canViewCoupons && (
                    <Box className="filter-item">
                      <label className="filter-label">Coupon Code</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={couponCode}
                          onChange={e => setCouponCode(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value="All" className="dropdown-option">
                            All
                          </MenuItem>
                          {couponList.length === 0 && couponLoading ? (
                            <MenuItem disabled className="dropdown-option">
                              Loading coupons...
                            </MenuItem>
                          ) : (
                            couponList.map(coupon => (
                              <MenuItem
                                key={coupon.id}
                                value={coupon.code}
                                className="dropdown-option"
                              >
                                {coupon.code}
                              </MenuItem>
                            ))
                          )}
                          {hasMoreCoupons && (
                            <MenuItem
                              onClick={loadMoreCoupons}
                              disabled={couponLoading}
                              sx={{
                                justifyContent: 'center',
                                fontStyle: 'italic',
                                color: '#666',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              {couponLoading ? 'Loading...' : 'Load More'}
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Buttons (Sticky Footer) */}
              <Box className="filter-buttons-container">
                <Button variant="outlined" onClick={handleClearAll} className="filter-clear-button">
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApplyFilter}
                  className="filter-apply-button"
                >
                  Apply
                </Button>
              </Box>
            </Popover>

            <Box className="request-table-container">
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    background: '#FFF',
                  }}
                >
                  <Tabs value={tabValue} onChange={handleTabChange} aria-label="request tabs">
                    <Tab
                      label="Academic Instructors"
                      aria-label="teacher tab"
                      sx={{ textTransform: 'none' }}
                      {...a11yProps(0)}
                    />
                    <Tab
                      label="Creative art Instructors"
                      aria-label="creative arts tab"
                      sx={{ textTransform: 'none' }}
                      {...a11yProps(1)}
                    />
                  </Tabs>
                </Box>

                {/* Active Filters Display */}
                {getActiveFilters().length > 0 && (
                  <Box className="active-filters-container">
                    {getActiveFilters().map((filter, index) => (
                      <Box key={index} className="filter-card">
                        <Typography className="filter-card-text">{filter.label}</Typography>
                        <CloseIcon
                          className="filter-card-close"
                          onClick={() => handleRemoveFilter(filter.type, filter.value)}
                        />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Sort By */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '8px',
                    px: 2,
                    py: 1,
                    background: 'transparent',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      color: '#14171F',
                    }}
                  >
                    Sort by
                  </Typography>
                  <Select
                    value={sortBy}
                    onChange={e => {
                      setSortBy(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                    sx={{
                      height: '30px',
                      minWidth: '160px',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '6px 8px 4px 8px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                        borderRadius: '8px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      background: '#FFF',
                    }}
                    renderValue={selected => {
                      if (!selected) return <span style={{ color: '#949DA6' }}>Select</span>;
                      if (selected === 'years_of_experience') return 'Years of experience';
                      if (selected === 'updated_at') return 'Updated at';
                      return selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="years_of_experience">Years of experience</MenuItem>
                    <MenuItem value="updated_at">Updated at</MenuItem>
                  </Select>
                  <Select
                    value={sortOrder}
                    onChange={e => {
                      setSortOrder(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                    disabled={!sortBy}
                    sx={{
                      height: '30px',
                      minWidth: '120px',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '6px 8px 4px 8px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                        borderRadius: '8px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      background: '#FFF',
                    }}
                    renderValue={selected => {
                      if (!selected) return <span style={{ color: '#949DA6' }}>Order</span>;
                      return selected === 'desc' ? 'Desc' : 'Asc';
                    }}
                  >
                    <MenuItem value="asc">Asc</MenuItem>
                    <MenuItem value="desc">Desc</MenuItem>
                  </Select>
                </Box>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <TableSkeleton />
                  </Box>
                ) : (
                  <>
                    <CustomTabPanel value={tabValue} index={0}>
                      <RequestTable
                        searchTerm={searchTerm}
                        level={levelToTeach}
                        category={category}
                        teacherRequestData={getCurrentData()}
                        value={
                          statusFilter === 'All'
                            ? 0
                            : statusFilter === 'approval_pending'
                              ? 1
                              : statusFilter === 'rejected'
                                ? 2
                                : statusFilter === 'approved'
                                  ? 3
                                  : 0
                        }
                        subject={selectedSubject}
                        tag={selectedTags.join(', ')}
                        yearsOfExperience={experience}
                        teachingLanguage={language}
                        city={city}
                        country={country}
                        gender={gender}
                        selectionMode={true}
                        selectedIds={selectedIds}
                        onSelectOne={handleSelectOne}
                        onSelectAll={handleSelectAllVisible}
                      />
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={1}>
                      <CreativeArtsRequestTable
                        searchTerm={searchTerm}
                        level={levelToTeach}
                        teacherRequestData={getCurrentData()}
                        value={
                          statusFilter === 'All'
                            ? 0
                            : statusFilter === 'approval_pending'
                              ? 1
                              : statusFilter === 'rejected'
                                ? 2
                                : statusFilter === 'approved'
                                  ? 3
                                  : 0
                        }
                        subject={selectedSubject}
                        tag={selectedTags.join(', ')}
                        yearsOfExperience={experience}
                        teachingLanguage={language}
                        city={city}
                        country={country}
                        gender={gender}
                        selectionMode={true}
                        selectedIds={selectedIds}
                        onSelectOne={handleSelectOne}
                        onSelectAll={handleSelectAllVisible}
                      />
                    </CustomTabPanel>
                  </>
                )}

                {/* Pagination UI */}
                {!loading && (
                  <Box className="request-pagination-container">
                    <Box className="request-pagination-info">
                      <Typography className="request-pagination-text">Showing</Typography>
                      <Select
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                        className="request-pagination-select"
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={15}>15</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={30}>30</MenuItem>
                      </Select>
                      <Typography className="request-pagination-text">
                        of {totalCount} Requests
                      </Typography>
                    </Box>
                    <nav className="request-pagination-nav">
                      <ul className="request-pagination-list">
                        {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                          let children = null;

                          if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                            children = <span className="request-pagination-ellipsis">…</span>;
                          } else if (type === 'page') {
                            children = (
                              <button
                                type="button"
                                className={`request-pagination-button ${selected ? 'selected' : ''}`}
                                {...item}
                              >
                                {itemPage}
                              </button>
                            );
                          } else {
                            children = (
                              <button type="button" className="request-pagination-button" {...item}>
                                {formatText(type)}
                              </button>
                            );
                          }

                          return (
                            <li key={index} className="request-pagination-item">
                              {children}
                            </li>
                          );
                        })}
                      </ul>
                    </nav>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Request;
